<template>
	<div class="group-settings-menu">
		<Popover ref="menu" :offsetY="28">
			<Grid2Icon
				halo
				type="solid"
				class="grid-icon"
				:class="{ active: $refs.menu && $refs.menu.isOpen }"
			/>
			<template #content>
				<Accessible
					name="settingsmenu"
					class="group-settings-options"
					trapped
					focusFirstItem
					autoFocus
					:wait="25"
					:tabOrder="[{ all: '.group-settings-option', clickOnEnter: true }]"
				>
					<component
						v-for="{ label, to, icon, className, callback } in options"
						:is="callback ? 'div' : 'RouterLink'"
						class="group-settings-option"
						:class="{ [className || '']: true }"
						:key="label"
						:to="to || null"
						@click="callback ? callback($event) : null"
					>
						<component :is="icon" type="solid" />
						<div class="group-settings-option-label" v-text="$t(label)" />
					</component>
				</Accessible>
			</template>
		</Popover>
	</div>
</template>

<script>
import { getChat } from '@/store'
import { Popover } from '@/components/common'
import { chatCan, ADD_MEMBER } from '@/helpers/permissions'
import Accessible from '@/components/common/Accessible'
import {
	Grid2Icon,
	BellIcon,
	UserGroupIcon,
	GearIcon,
	FilesIcon,
	PhotoFilmIcon,
	LinkIcon,
	SearchIcon,
	ThumbtackIcon,
	MessageDotIcon,
	MessageCheckIcon,
	SquarePollHorizontalIcon,
} from '@/icons'
import { orgFlag, SEARCH, POLLS } from '@/helpers/flags'
import { markChatAsRead, markChatAsUnread } from '@/api/membership'

export default {
	name: 'SettingsMenu',
	components: { Popover, Grid2Icon, Accessible },
	props: { chatId: [Number, String] },
	data() {
		return {
			chat: getChat(this.chatId),
		}
	},
	mounted() {
		this.chat = getChat(this.chatId)
	},
	watch: {
		chatId(chatId) {
			this.chat = getChat(chatId)
		},
	},
	computed: {
		isDm() {
			return this.chat?.isdm || false
		},
		canInvite() {
			return !this.isDm && chatCan(this.chat, ADD_MEMBER)
		},
		canSearch() {
			return orgFlag(SEARCH)
		},
		canCreatePolls() {
			return orgFlag(POLLS)
		},
		hasPinnedMessage() {
			return this.chat?.pinned_message_id
		},
		options() {
			const id = this.chatId
			const { marked_unread } = this.chat || {}
			return [
				{
					label: 'groupsettings_notifications_title',
					to: { name: 'groupnotifs', params: { id } },
					icon: BellIcon,
				},
				!this.isDm && {
					label: 'admin_groups_members_label',
					to: { name: 'grouppeople', params: { id } },
					icon: UserGroupIcon,
				},
				{
					label: 'groupsettings_bubblesettings_title',
					to: { name: 'groupoptions', params: { id } },
					icon: GearIcon,
				},
				{
					label: 'groupsettings_mediatray_files_title',
					to: { name: 'groupmedia', params: { id, type: 'files' } },
					icon: FilesIcon,
				},
				{
					className: 'large-icon',
					label: 'groupsettings_mediatray_photos_videos_title',
					to: { name: 'groupmedia', params: { id, type: 'photos' } },
					icon: PhotoFilmIcon,
				},
				this.canCreatePolls && {
					className: 'large-icon',
					label: 'groupsettings_mediatray_polls_title',
					to: { name: 'groupmedia', params: { id, type: 'polls' } },
					icon: SquarePollHorizontalIcon,
				},
				this.canInvite && {
					className: 'large-icon',
					label: 'groupinvites_title_invite',
					to: { name: 'groupinvites', params: { id } },
					icon: LinkIcon,
				},
				this.canSearch && {
					className: 'large-icon',
					label: 'admin_people_search_action',
					to: { name: 'search', query: { chats: id, focus: 1 } },
					icon: SearchIcon,
				},
				this.hasPinnedMessage && {
					className: 'large-icon',
					label: 'chat_pin_message_title',
					icon: ThumbtackIcon,
					callback: this.openPinnedMessage,
				},
				{
					className: 'large-icon',
					label: marked_unread
						? 'chat_drawer_action_markread'
						: 'chat_drawer_action_markunread',
					icon: marked_unread ? MessageCheckIcon : MessageDotIcon,
					callback: this.toggleMarkAsRead,
				},
			].filter((x) => x)
		},
	},
	methods: {
		openPinnedMessage() {
			EventBus.$emit('open-pinned-message', this.chatId)
		},

		toggleMarkAsRead() {
			this.chat?.marked_unread ? this.markAsRead() : this.markAsUnread()
		},

		async markAsRead() {
			try {
				this.$refs.menu?.closePopover?.()
				await markChatAsRead(this.chatId)
				this.chat = getChat(this.chatId)
			} catch (e) {
				this.$alert.error(e)
			}
		},

		async markAsUnread() {
			try {
				this.$refs.menu?.closePopover?.()
				await markChatAsUnread(this.chatId)
				this.chat = getChat(this.chatId)
			} catch (e) {
				this.$alert.error(e)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-settings-menu {
	.grid-icon {
		cursor: pointer;
		color: var(--neutral-400);
		font-size: 20px;
		margin-top: -0.8em;
		margin-left: -0.4em;
		&::before {
			opacity: 0;
		}
		&:hover {
			&::before {
				opacity: 0.1;
			}
		}
	}
	:deep(.popover-trigger) {
		display: block;
		height: 20px;
		width: 20px;
		&.open {
			.grid-icon {
				&::before {
					opacity: 0.1;
				}
			}
		}
	}
}

.group-settings-options {
	@include flex;
	flex-wrap: wrap;
	row-gap: 16px;
	width: 332px;
	padding: 4px;
	background: var(--neutral-0);
	box-shadow: 0px 2px 17px 0px rgba(200, 200, 200, 0.6);
	text-align: center;

	.group-settings-option {
		@include flex-column-center(8px);
		width: 108px;
		text-decoration: none;
		padding: 6px 8px;
		border-radius: 6px;
		cursor: pointer;

		&:hover {
			background: var(--neutral-100);
		}

		.pronto-icon {
			color: var(--neutral-400);
			font-size: 24px;
		}
		.group-settings-option-label {
			color: var(--neutral-600);
			font-size: 12px;
		}

		&.large-icon {
			.pronto-icon {
				font-size: 24px;
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-settings-options {
		box-shadow: 0 8px 24px 0 #000;
	}
}
</style>
